import { Exception } from '@ch-types/Exception';

import axios from 'axios';
import _ from 'lodash';
import $ from 'jquery';

interface IOptionsType {
    url: string;
    [x: string]: any;
}

/**
 * Attempts to grab a file from a specific URL and returns the response. Contains utility functions with varying levels
 * of abstraction.
 * Examples:
 *   - Simple http request
 *     remoteFileLoader.load({
 *      url: '/my-resource.html'
 *     })
 *     .then(function(response) {
 *      $log.debug(response.data);
 *     })
 *     .catch(function(response) {
 *      $log.debug('error', response.data);
 *     });
 *
 *  - Load JSON
 *    remoteFileLoader.load({
 *      url: '/my-resource.json'
 *    })
 *    .then(function(jsonObject) {
 *      $log.debug('Json object');
 *    })
 *    .catch(function(response) {
 *      // You get the whole http response here so you can deal with the error
 *    });
 *
 * - Load script
 *  remoteFileLoader.loadScript({
 *      url: 'my-script.js'
 *  })
 *  .then(function() {
 *      $log.debug('Script loaded successfully');
 *  })
 *  .catch(function(e) {
 *      $log.error('Script did not load', e);
 *  });
 *
 * - Load script once
 *  remoteFileLoader.loadScriptOnce({
 *      url: 'my-script.js'
 *  })
 *  .then(function() {
 *      $log.debug('Script loaded successfully');
 *  })
 *  .catch(function(e) {
 *      $log.error('Script did not load', e);
 *  });
 */
export class RemoteFileLoader {
    public static loadOnceScriptPromises = {};

    /**
     * Loads a JSON resource using the URL from the provided options. Returns a promise that resolves with
     * the decoded JSON and rejects with the raw response.
     * @param {object} options - Object with required key "url"
     * @return {Promise} JSON object on success / raw error on failure.
     */
    public static async loadJson(options: IOptionsType): Promise<any> {
        try {
            const response = await RemoteFileLoader.load(options);
            const data = response.data;
            if (!_.isObject(data)) {
                console.warn('remoteFileLoader: Got invalid JSON', data);
                return Promise.reject(new Exception({ message: 'Got invalid JSON: ' + data }));
            }
            return data;
        } catch (error) {
            return Promise.reject(error);
        }
    }

    /**
     * Loads a resource using the URL from the provided options. Returns a promise that resolves/rejects with
     * the raw http response.
     * @param {object} options - Object with required key "url"
     * @return {Promise} Raw response on success / raw error on failure.
     */
    public static async load(options: IOptionsType): Promise<any> {
        const url = options.url;
        console.debug('remoteFileLoader: Loading remote file from url', url);
        try {
            return await axios({
                method: 'GET',
                url,
            });
        } catch (error) {
            console.warn('remoteFileLoader: Got an error while loading URL', url, error);
            return Promise.reject(error);
        }
    }

    /**
     * Loads a script and returns a promise for it.
     * @param {object} options Object with required key "url"
     * @return {Promise} - resolves on successful load / rejects on error.
     */
    public static loadScript(options: IOptionsType): Promise<any> {
        const ajaxOptions = $.extend({}, options, {
            dataType: 'script',
        });

        // wrap jquery promise in a $q promise
        return Promise.resolve($.ajax(ajaxOptions));
    }

    /**
     * Loads a script only once and returns a promise for it.
     * Note that this loads the script once within this angular application and assumes that callers
     * are only using this loader to load the script (doesn't check if the script has been loaded elsewhere).
     * Also note that if you add cache buster query strings, this function will honor them.
     *
     * @param {object} options Object with required key "url"
     * @return {Promise} - resolves on successful load / rejects on error.
     */
    public static async loadScriptOnce(options: IOptionsType): Promise<any> {
        const scriptSrc = options.url;
        console.debug('remoteFileLoader: Loading script for URL', scriptSrc);
        if (RemoteFileLoader.loadOnceScriptPromises[scriptSrc]) {
            console.debug('remoteFileLoader: Ignoring request, script previously queued for', scriptSrc);
            return RemoteFileLoader.loadOnceScriptPromises[scriptSrc];
        }
        try {
            RemoteFileLoader.loadOnceScriptPromises[scriptSrc] = await RemoteFileLoader.loadScript(options);
            console.debug('remoteFileLoader: Successfully loaded script', scriptSrc);
            return RemoteFileLoader.loadOnceScriptPromises[scriptSrc];
        } catch (error) {
            console.error('remoteFileLoader: Error loading script', scriptSrc, error);
            RemoteFileLoader.loadOnceScriptPromises[scriptSrc] = null;
            return Promise.reject(error);
        }
    }
}
